import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, useTheme, Stack, Button } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import { apiClient, handleApiError } from '../../../services/apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../../../config/apiEndpoints';

interface AveragePriceChartProps {
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
}

const AveragePriceChart: React.FC<AveragePriceChartProps> = ({ startDate, endDate }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [metricType, setMetricType] = useState<'price' | 'km'>('price');
  const [statsData, setStatsData] = useState<{
    avgPricePerKm?: number | null;
    avgDistancePerTruck?: number | null;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStatsData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      try {
        const daysDiff = endDate.diff(startDate, 'day') + 1;
        const daysParam = Math.min(daysDiff, 60); // API max is 60 days

        const url = buildApiUrl(API_ENDPOINTS.stats.offers);
        const params = new URLSearchParams();
        params.append('days', daysParam.toString());
        const response = await apiClient(url + '?' + params, { method: 'GET' });
        const data = await response.json();

        if (data.result) {
          setStatsData(data.result);
        }
      } catch (error) {
        handleApiError(error, t('Failed to fetch stats data'));
        setStatsData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStatsData();
  }, [startDate, endDate, t]);


  const lineColor = '#8B5CF6';
  const textColor = theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.purple;
  const gridColor = theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)';

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: { xs: 2, sm: 3 },
        border: (theme: any) => `1px solid ${theme.palette.border.default}`,
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.background.white : 'transparent',
        overflow: 'hidden',
        boxShadow: 'none',
      }}
      variant="outlined"
    >
      <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 2 },
            py: { xs: 1, sm: 1.25, md: 1.5 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            position: 'relative',
            bgcolor: 'transparent',
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: textColor,
              fontSize: (theme: any) => theme.typography.fontSizes.heading2,
              letterSpacing: '0.2px',
              flex: { xs: '1 1 auto', sm: 'initial' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {metricType === 'price' ? t('Average price per km') : t('Average km driven')}
          </Typography>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              position: { xs: 'static', sm: 'absolute' },
              left: { sm: '50%' },
              transform: { sm: 'translateX(-50%)' },
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: lineColor,
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: textColor,
                fontWeight: 500,
              }}
            >
              {metricType === 'price'
                ? statsData?.avgPricePerKm
                  ? `€${statsData.avgPricePerKm.toFixed(2)}/km`
                  : t('No data')
                : statsData?.avgDistancePerTruck
                  ? `${Math.round(statsData.avgDistancePerTruck)} km`
                  : t('No data')}
            </Typography>
          </Stack>

          <Button
            onClick={() => setMetricType(metricType === 'price' ? 'km' : 'price')}
            variant="outlined"
            sx={{
              position: { xs: 'static', sm: 'absolute' },
              right: { sm: 5.25, md: 8.5 },
              alignSelf: { xs: 'flex-end', sm: 'auto' },
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#8B5CF6',
              fontWeight: 500,
              textTransform: 'none',
              minWidth: 'auto',
              px: 2,
              py: 0.75,
              borderColor: theme.palette.mode === 'dark' ? '#ffffff' : '#8B5CF6',
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : 'rgba(139, 92, 246, 0.05)',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(139, 92, 246, 0.1)',
                borderColor: theme.palette.mode === 'dark' ? '#ffffff' : '#8B5CF6',
              },
            }}
          >
            {metricType === 'price' ? t('Show km driven') : t('Show price per km')}
          </Button>

        </Box>

        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 2 },
            pb: { xs: 1, sm: 1.25, md: 1.5 },
            pt: 0,
            width: '100%',
            flex: 1,
            minHeight: 0,
            bgcolor: 'transparent',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: textColor }}>{t('Loading...')}</Typography>
            </Box>
          ) : statsData ? (
            <LineChart
              series={[
                {
                  data: [
                    metricType === 'price' 
                      ? statsData.avgPricePerKm || 0
                      : statsData.avgDistancePerTruck || 0
                  ],
                  label: metricType === 'price' ? t('Average Price per km') : t('Average Distance per Truck'),
                  color: lineColor,
                  curve: 'monotoneX',
                  showMark: true,
                },
              ]}
              sx={{
                backgroundColor: 'transparent',
                '& .MuiChartsLegend-root': {
                  display: 'none',
                },
                '& .MuiChartsGrid-root': {
                  stroke: gridColor,
                },
                '& .MuiChartsAxis-root': {
                  stroke: gridColor,
                },
                '& .MuiMarkElement-root': {
                  display: 'none',
                },
                '& .MuiChartsTooltip-root': {
                  '& .MuiMarkElement-root': {
                    display: 'block',
                  },
                },
                '& .MuiLineElement-root': {
                  strokeWidth: 2.5,
                  strokeDasharray: '2000',
                  strokeDashoffset: '2000',
                  animation: 'drawLine 3s ease-out forwards',
                  '@keyframes drawLine': {
                    'to': {
                      strokeDashoffset: '0',
                    },
                  },
                },
                '& .MuiChartsTooltip-root .MuiMarkElement-root': {
                  r: 4.5,
                },
              }}
              xAxis={[
                {
                  scaleType: 'point',
                  data: [t('Average')],
                  tickLabelStyle: {
                    fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: 12,
                  },
                },
              ]}
              yAxis={[
                {
                  tickLabelStyle: {
                    fill: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: 12,
                  },
                  valueFormatter: (value: number) => {
                    if (metricType === 'price') {
                      return `€${value.toFixed(2)}`;
                    } else {
                      return `${Math.round(value)} km`;
                    }
                  },
                },
              ]}
              grid={{ horizontal: true, vertical: false }}
              slotProps={{
                legend: {
                  position: { vertical: 'top', horizontal: 'center' },
                },
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: textColor }}>{t('No data available')}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AveragePriceChart;
